import {
	Box,
	CircularProgress,
	SxProps,
	Tab,
	Tabs,
	Theme,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Modal from "../../../common/components/Modal";
import { PrimaryButton } from "../../../common/components/CustomButton";
import { useAppDispatch } from "../../../common/hooks/default";
import { Tender } from "../tender.module";
import { loadExtruderFeedback } from "../tender.thunk";
import { resetExtruderFeedback } from "../tenderSlice";
import {
	selectExtruderFeedback,
	selectExtruderFeedbackError,
	selectExtruderFeedbackLoading,
} from "../tender.selector";

const MODAL_PAPER_SX: SxProps<Theme> = {
	width: "50vw",
	height: "50vh",
	maxWidth: "50vw",
	maxHeight: "50vh",
};

const style: { [key: string]: SxProps<Theme> } = {
	tabsBar: {
		borderBottom: 1,
		borderColor: "divider",
	},
	tabPanel: {
		paddingTop: "16px",
	},
	pre: {
		maxHeight: "calc(50vh - 220px)",
		overflow: "auto",
		margin: 0,
	},
	loaderBox: {
		display: "flex",
		justifyContent: "center",
		padding: "32px",
	},
	errorBox: {
		color: "error.main",
		padding: "16px",
	},
	emptyBox: {
		padding: "16px",
	},
};

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`extruder-feedback-tabpanel-${index}`}
		>
			{value === index && <Box sx={style.tabPanel}>{children}</Box>}
		</div>
	);
}

interface ExtruderFeedbackModalProps {
	tender: Partial<Tender> | Tender;
	isDialogOpen: boolean;
	onClose: Dispatch<SetStateAction<boolean>>;
}

const ExtruderFeedbackModal = ({
	tender,
	isDialogOpen,
	onClose,
}: ExtruderFeedbackModalProps) => {
	const dispatch = useAppDispatch();
	const submissionId = tender.latest_extruder_submission?.submission_id;
	const feedback = useSelector(selectExtruderFeedback);
	const loading = useSelector(selectExtruderFeedbackLoading);
	const error = useSelector(selectExtruderFeedbackError);
	const [activeTab, setActiveTab] = useState(0);

	useEffect(() => {
		if (!isDialogOpen || !submissionId) return;
		setActiveTab(0);
		dispatch(loadExtruderFeedback(submissionId));
	}, [dispatch, isDialogOpen, submissionId]);

	const handleClose = () => {
		dispatch(resetExtruderFeedback());
		onClose(false);
	};

	const assetIds = feedback ? Object.keys(feedback) : [];
	const siteNameByAssetId = new Map<string, string>(
		(tender.sites ?? [])
			.filter((s) => !!s.asset_id)
			.map((s) => [s.asset_id as string, s.specific_name || s.name])
	);

	return (
		<Modal
			title="Extruder feedback"
			onClose={handleClose}
			open={isDialogOpen}
			actions={
				<PrimaryButton
					onClick={handleClose}
					text="Close"
					type="button"
					color="primary"
				/>
			}
			maxWidth={false}
			sx={MODAL_PAPER_SX}
		>
			{loading && (
				<Box sx={style.loaderBox}>
					<CircularProgress />
				</Box>
			)}
			{!loading && error && <Box sx={style.errorBox}>{error}</Box>}
			{!loading && !error && assetIds.length === 0 && (
				<Box sx={style.emptyBox}>No metadata feedback available.</Box>
			)}
			{!loading && !error && assetIds.length > 0 && (
				<>
					<Box sx={style.tabsBar}>
						<Tabs
							value={activeTab}
							onChange={(_e: React.SyntheticEvent, v: number) =>
								setActiveTab(v)
							}
							variant="scrollable"
							scrollButtons="auto"
							aria-label="extruder feedback tabs"
						>
							{assetIds.map((assetId) => (
								<Tab
									key={assetId}
									label={
										siteNameByAssetId.get(assetId) ||
										assetId
									}
								/>
							))}
						</Tabs>
					</Box>
					{assetIds.map((assetId, index) => (
						<CustomTabPanel
							key={assetId}
							value={activeTab}
							index={index}
						>
							<pre style={style.pre as React.CSSProperties}>
								{JSON.stringify(feedback?.[assetId], null, 2)}
							</pre>
						</CustomTabPanel>
					))}
				</>
			)}
		</Modal>
	);
};

export default ExtruderFeedbackModal;
